import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';

@Component({
    selector: 'app-paper-guide-camera',
    templateUrl: './paper-guide-camera.component.html',
    styleUrls: ['./paper-guide-camera.component.css'],
    standalone: false
})
export class PaperGuideCameraComponent implements OnDestroy {
  @ViewChild('papelVideo') papelVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('papelCanvas') papelCanvas?: ElementRef<HTMLCanvasElement>;

  @Input() photoBase64: string | null = null;
  @Output() photoBase64Change = new EventEmitter<string>();

  papelCameraActive = false;
  private papelCameraStream: MediaStream | null = null;

  ngOnDestroy(): void {
    this.stopPapelCamera();
  }

  async startPapelCamera(): Promise<void> {
    if (this.papelCameraActive) return;

    const stream = await this.acquireCameraStream();
    if (!stream) {
      alert('No se pudo acceder a la cámara. Verifica los permisos del navegador.');
      return;
    }

    this.papelCameraStream = stream;
    this.papelCameraActive = true;
    setTimeout(() => this.attachPapelCameraStream(), 0);
  }

  capturePapelPhoto(): void {
    const video = this.papelVideo?.nativeElement;
    const canvas = this.papelCanvas?.nativeElement;
    if (!video || !canvas) return;

    const context = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
    if (!context || !video.videoWidth || !video.videoHeight) return;

    const maxWidth = 300;
    const maxHeight = 200;
    let width = video.videoWidth;
    let height = video.videoHeight;
    if (width > height) {
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
    } else if (height > maxHeight) {
      width = Math.round((width * maxHeight) / height);
      height = maxHeight;
    }

    canvas.width = width;
    canvas.height = height;
    context.drawImage(video, 0, 0, width, height);
    this.photoBase64Change.emit(canvas.toDataURL('image/jpeg', 0.6));
    this.stopPapelCamera();
  }

  cancelPapelCamera(): void {
    this.stopPapelCamera();
  }

  retryPapelPhoto(): void {
    this.photoBase64Change.emit('');
    void this.startPapelCamera();
  }

  private attachPapelCameraStream(retry = 0): void {
    if (!this.papelCameraStream) return;
    const video = this.papelVideo?.nativeElement;
    if (!video) {
      if (retry < 10) setTimeout(() => this.attachPapelCameraStream(retry + 1), 50);
      return;
    }
    video.srcObject = this.papelCameraStream;
    void video.play().catch(() => undefined);
  }

  private stopPapelCamera(): void {
    if (this.papelCameraStream) {
      this.papelCameraStream.getTracks().forEach((track) => track.stop());
      this.papelCameraStream = null;
    }
    this.papelCameraActive = false;
  }

  private async acquireCameraStream(): Promise<MediaStream | null> {
    if (!navigator.mediaDevices?.getUserMedia) return null;
    const attempts: MediaStreamConstraints[] = [
      { video: { facingMode: { ideal: 'environment' } }, audio: false },
      { video: { facingMode: 'user' }, audio: false },
      { video: true, audio: false },
    ];

    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch {
        // Try the next constraint set for devices that do not expose a rear camera.
      }
    }
    return null;
  }
}


